import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const TASK_YAML_EXAMPLE = `taskKey: explains-double-cleanse
suite: capability            # capability | regression
input: What is double cleansing and why do people do it?
expectTools: [kb-read]
# expectNoTools: true        # for should-NOT-call cases (instead of expectTools)
# expectAnswer: double cleanse   # required if you use the answer-contains grader
grade:
  - tool-selection
  # - answer-contains
  - judge:
      rubric: |
        Pass only if the answer describes an oil-based cleanse followed by a
        water-based cleanse.
      reference: |           # optional facts the judge may rely on
        Double cleansing = an oil/balm cleanser, then a water-based cleanser.
trials: 1                    # repeat the task up to N times (default 1)
passThreshold: 1             # pass once M of the trials pass (default 1)
`;

export function EvalsGuidePage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Evals guide</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          What evaluation is, how a run works, how suites are structured, and how to write a task.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">What evaluation is</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            An eval measures how well an agent <strong>reasons</strong>: does it call the right tool
            (and <em>not</em> call one when it shouldn&apos;t), and is its answer grounded in what
            it retrieved?
          </p>
          <p>
            It runs the <strong>real production agent loop</strong> — the same execution engine,
            tool implementations, and persistence that serve live traffic — against a set of tasks,
            using the model, system prompt, and enabled tools defined by the suite, then grades the
            outcome. It is not a mock or a unit test; every run makes real model calls and records
            the full transcript.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">How a run works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Start a run from a suite&apos;s page (it runs in the background) or from the command
            line (<code>pnpm eval --suite &lt;name&gt;</code>). Each task runs the agent in a fresh
            session against real Gemini, then grades it:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Deterministic graders first</strong> — <code>tool-selection</code> (were the
              expected tools called, or none?) and <code>answer-contains</code> (does the answer
              contain an expected string?). These spend no model tokens.
            </li>
            <li>
              <strong>An LLM judge</strong> only for open-ended grounding — it reads a rubric and
              the answer and returns pass/fail with a reason.
            </li>
          </ul>
          <p>
            A task passes only if <em>every</em> grader passes. Flaky tasks can repeat (
            <code>trials</code> / <code>passThreshold</code>, i.e. pass@k). Results — overall and
            per-tag pass rates, each task&apos;s pass/fail with the failing grader&apos;s reason,
            and the full transcript — appear under <strong>Runs</strong>. Only one run executes at a
            time; a second trigger while one is running is refused.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">How it&apos;s structured</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            A <strong>suite</strong> is one agent version — a model, a system prompt, and a set of
            enabled tools — plus its <strong>tasks</strong>. Editing a suite&apos;s configuration is
            a form; each task is written as YAML (below).
          </p>
          <p>Tag each task:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>capability</strong> — a skill you&apos;re trying to improve; may start below
              100%.
            </li>
            <li>
              <strong>regression</strong> — should stay passing. Add one whenever you fix a real
              failure, so it can&apos;t come back.
            </li>
          </ul>
          <p>
            A <strong>run</strong> executes a suite and records one result per task. Keep suites
            balanced — include tasks where a tool <em>should</em> fire and tasks where it{' '}
            <em>shouldn&apos;t</em>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Writing a task (YAML)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>A task is written as YAML. Fields:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <code>taskKey</code> — a unique name for the task within the suite.
            </li>
            <li>
              <code>suite</code> — the tag: <code>capability</code> or <code>regression</code>.
            </li>
            <li>
              <code>input</code> — the user message sent to the agent.
            </li>
            <li>
              <code>expectTools</code> — tools the agent should call (optional).
            </li>
            <li>
              <code>expectNoTools</code> — <code>true</code> if the agent should call no tools
              (optional).
            </li>
            <li>
              <code>expectAnswer</code> — a string the answer must contain (optional; required by
              the <code>answer-contains</code> grader).
            </li>
            <li>
              <code>grade</code> — one or more graders (see below).
            </li>
            <li>
              <code>trials</code> / <code>passThreshold</code> — repeat up to <code>trials</code>{' '}
              times, pass once <code>passThreshold</code> pass (both default 1).
            </li>
          </ul>
          <p>
            <strong>Graders</strong> (any combination):
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <code>tool-selection</code> — needs <code>expectTools</code> or{' '}
              <code>expectNoTools</code>.
            </li>
            <li>
              <code>answer-contains</code> — needs <code>expectAnswer</code>.
            </li>
            <li>
              <code>judge</code> with a <code>rubric</code> (and optional <code>reference</code>) —
              the judge sees only the rubric, the question, the answer, and the reference, so{' '}
              <strong>put the expected facts in the rubric or reference</strong>; it cannot see the
              tool output.
            </li>
          </ul>
          <p className="text-[var(--color-muted-foreground)]">
            Rules enforced on save: <code>answer-contains</code> requires <code>expectAnswer</code>;{' '}
            <code>tool-selection</code> requires <code>expectTools</code> or{' '}
            <code>expectNoTools</code>; <code>passThreshold</code> must be ≤ <code>trials</code>.
          </p>
          <pre className="overflow-x-auto rounded-md bg-[var(--color-muted)] p-3 font-mono text-xs">
            {TASK_YAML_EXAMPLE}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
